import { createTheme } from '@mui/material/styles'

/**
 * MUI theme mapped from the legacy AngularJS frontend's visual language
 * (Materialize CSS + Bootstrap 3, compiled via `app/assets/sass`).
 *
 * All values below were extracted from the real SCSS source instead of
 * guessed — see `client/src/theme/PALETTE.md` for the full mapping of
 * each token back to its original SCSS file/variable.
 *
 * Source files consulted:
 * - app/assets/sass/components/_variables.scss (Materialize `$primary-color`,
 *   `$secondary-color`, `$success-color`, `$error-color`, `$link-color`,
 *   typography scale, sidenav tokens)
 * - app/assets/sass/components/_color.scss (Materialize color maps: purple,
 *   green, red, light-blue, grey shades used by the variables above)
 * - app/assets/sass/components/_typography.scss (base font stack, heading
 *   sizes, base text color `$off-black`)
 * - app/assets/sass/components/_roboto.scss (`@font-face` declarations —
 *   confirms Roboto is the actual font, matching MUI's default)
 * - app/assets/sass/app.scss (`.wraper-login { background-color: #6a1b9a }`
 *   confirms `purple darken-3` (#6a1b9a) is the real brand color in use,
 *   not just a Materialize default left unchanged)
 */

const theme = createTheme({
  palette: {
    primary: {
      // $primary-color / $secondary-color: color('purple', 'darken-3')
      main: '#6a1b9a',
      light: '#8e24aa', // purple darken-1 (lighten($primary-color, 15%) lands close to this shade)
      dark: '#4a148c', // purple darken-4 (darken($primary-color, 15%) lands close to this shade)
      contrastText: '#ffffff', // $button-raised-color / $collapsible-header-color
    },
    secondary: {
      // Materialize's $secondary-color equals $primary-color in this app
      // (both set to purple darken-3); MUI needs a distinct secondary role,
      // so the accent used for links/interactive highlights is mapped here.
      main: '#039be5', // $link-color: color('light-blue', 'darken-1')
      light: '#4fc3f7', // light-blue lighten-2
      dark: '#0277bd', // light-blue darken-3
      contrastText: '#ffffff',
    },
    error: {
      // $error-color: color('red', 'base')
      main: '#f44336',
    },
    success: {
      // $success-color: color('green', 'base')
      main: '#4caf50',
    },
    text: {
      // $off-black: rgba(0, 0, 0, 0.87) — base body text color in _typography.scss
      primary: 'rgba(0, 0, 0, 0.87)',
    },
    background: {
      // Legacy `body` has no explicit background-color (plain white) — the
      // Materialize `#f5f5f5` token only styled input/autofill backgrounds
      // in app.scss, not the page itself. Using it here made `CssBaseline`
      // paint the whole app on a grey backdrop instead of matching
      // production's white page.
      default: '#ffffff',
      paper: '#ffffff', // $card-bg-color / $sidenav-bg-color / $collection-bg-color
    },
    divider: '#e0e0e0', // $collection-border-color / grey lighten-2
  },
  typography: {
    // _typography.scss: `font-family: "Roboto", sans-serif;` served locally
    // via @font-face in _roboto.scss (Roboto Thin/Light/Regular/Medium/Bold).
    fontFamily: ['Roboto', 'sans-serif'].join(','),
    h1: { fontWeight: 400, fontSize: '4.2rem' }, // $h1-fontsize
    h2: { fontWeight: 400, fontSize: '3.56rem' }, // $h2-fontsize
    h3: { fontWeight: 400, fontSize: '2.92rem' }, // $h3-fontsize
    h4: { fontWeight: 400, fontSize: '2.28rem' }, // $h4-fontsize
    // $h5-fontsize; used app-wide as the page-title convention (every
    // module's `<Typography variant="h5" component="h2">` heading) —
    // bolded (unlike legacy's uniform h1-h6 400 weight) so the page title
    // reads as prominent, matching the legacy page title's visual weight.
    h5: { fontWeight: 700, fontSize: '1.64rem' },
    h6: { fontWeight: 400, fontSize: '1rem' }, // $h6-fontsize
    button: {
      textTransform: 'uppercase', // .btn { text-transform: uppercase } in _buttons.scss
    },
  },
  shape: {
    radius: 2, // $button-radius / $select-radius
  },
  components: {
    // Legacy inputs (Materialize `.input-field`) are underlined, not boxed
    // — MUI's own default variant since v5 is "outlined", which is what
    // made every form field across the app render inside a full border
    // instead of matching production's minimal underline style. No module
    // sets an explicit `variant` on `TextField`, so this default alone
    // covers every filter/form field app-wide.
    MuiTextField: {
      defaultProps: {
        variant: 'standard',
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 2, // $button-radius: 2px
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          // $card-bg-color / $sidenav-bg-color
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        // Legacy list tables use native `<th>`, bold by the browser's own
        // default table styles; MUI's `TableCell` has no such default, so
        // header rows rendered as plain-weight text without this override.
        head: {
          fontWeight: 700,
        },
      },
    },
  },
})

export default theme
